import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Button, Flex, Spin, Table, Tooltip, Typography } from 'antd'
import { DiffOutlined, FilterOutlined, SolutionOutlined } from '@ant-design/icons'

import { BulkDataTypesEnum, PATHS, RoleEnum, Route, RoutesFilters } from '@types'
import { useGeneral, useRoutesFilters, useUser } from '@contexts'

import { ContentContainer, StyledButton, StyledRow, StyledSelect } from '../StyledComponents'
import BulkDataUploadModal from '../BulkDataUpload'
import { useRoutes } from './hooks'
import { useRoutesPagination } from './hooks/pagination'
import { useContracts, useEquipment } from '../../hooks'
import arrayToOptions from '../../utils/array-to-options'
import removeProperties from '../../utils/remove-properties'

const { Text } = Typography

const RoutesDashboard = () => {
  const { filters, setFilters } = useRoutesFilters()
  const { t } = useTranslation()
  const { routes, isRoutesLoading, routesFilters, routesFiltersIsLoading } = useRoutes(filters)
  const { asnums: contractsAsnums } = useContracts()
  const { equipmentOptions } = useEquipment()
  const { filtersDisplayed, handleDisplayFilters, menuDisplayed } = useGeneral()
  const pagination = useRoutesPagination({ filters, setFilters }, routes?.total)
  const [bulkDataUploadModalIsVisible, setBulkDataUploadModalIsVisible] = useState(false)
  const { state } = useUser()
  const roles = state?.roles.map(role => role.name)
  const { ADMINRO, PROCUREMENT } = RoleEnum
  const location = useLocation()
  const navigate = useNavigate()

  const getContractsPath = (asnum: number) => {
    let base = PATHS.ADMIN.CONTRACTS
    if (roles?.includes(ADMINRO)) base = PATHS.ADMINRO.CONTRACTS
    else if (roles?.includes(PROCUREMENT)) base = PATHS.PROCUREMENT.CONTRACTS
    return `${base}?service=${asnum}`
  }

  const handleViewContractsClick = (asnum: number, e: React.MouseEvent) => {
    e.preventDefault()
    navigate(getContractsPath(asnum))
  }

  const clearAllFilters = useCallback(() => {
    setFilters((prevState: RoutesFilters) => ({
      ...removeProperties(prevState, 'asnum'),
      page: 1,
    }))
  }, [setFilters])

  const handleClearFilters = useCallback(() => {
    clearAllFilters()
  }, [clearAllFilters])

  const handleAsnumFilterChange = useCallback(
    (value: any) => {
      if (value) {
        setFilters(({ limit }: RoutesFilters) => ({
          limit,
          page: 1,
          asnum: value as number,
        }))
      } else {
        handleClearFilters()
      }
    },
    [setFilters, handleClearFilters],
  )

  const handleEquipmentFilterChange = (value: any) => {
    if (value) {
      setFilters(({ asnum, ...prevState }: RoutesFilters) => ({
        ...prevState,
        page: 1,
        equipment: value as string,
      }))
    } else {
      handleClearFiltersEquipment()
    }
  }

  const columns = [
    {
      title: () => (
        <Tooltip placement="topLeft" title={t('admin_router.header.service_number')}>
          {t('admin_router.header.service_num.')}
        </Tooltip>
      ),
      dataIndex: 'asnum',
      key: 'numServicio',
      render: (asnum: number) => (
        <Flex className="asnum-cell">
          <Text>{asnum}</Text>
          {contractsAsnums?.includes(asnum.toString()) ? (
            <Tooltip placement="topLeft" title={t('admin_router.header.see_contracts')}>
              <Link to={getContractsPath(asnum)} onClick={e => handleViewContractsClick(asnum, e)}>
                <SolutionOutlined className="option-button" />
              </Link>
            </Tooltip>
          ) : (
            ''
          )}
        </Flex>
      ),
    },
    {
      title: () => (
        <Tooltip placement="topLeft" title={t('admin_router.header.file_class')}>
          {t('admin_router.header.file_class')}
        </Tooltip>
      ),
      dataIndex: 'vsart',
      key: 'clase',
    },
    {
      title: () => (
        <Tooltip placement="topLeft" title={t('admin_router.header.product')}>
          {t('admin_router.header.prod')}
        </Tooltip>
      ),
      dataIndex: 'add01',
      key: 'producto',
    },
    {
      title: () => (
        <Tooltip placement="topLeft" title={t('admin_router.header.equipment_type')}>
          {t('admin_router.header.eq_type')}
        </Tooltip>
      ),
      dataIndex: 'add02',
      key: 'tipoEquipo',
    },
    {
      title: t('admin_router.header.origin'),
      key: 'origen',
      children: [
        {
          title: t('admin_router.header.country'),
          dataIndex: 'land1a',
          key: 'pais',
        },
        {
          title: t('admin_router.header.cp'),
          dataIndex: 'pstlza',
          key: 'cpOrigen',
        },
        {
          title: t('admin_router.header.city'),
          dataIndex: 'zcityCodea',
          key: 'ciudadOrigen',
        },
        {
          title: t('admin_router.header.zone'),
          dataIndex: 'trfzna',
          key: 'zonaOrigen',
        },
        {
          title: t('admin_router.header.region'),
          dataIndex: 'regioa',
          key: 'regionOrigen',
        },
        {
          title: () => (
            <Tooltip placement="topLeft" title={t('admin_router.header.storage_tank')}>
              {t('admin_router.header.storage_tank')}
            </Tooltip>
          ),
          key: 'deposito',
          render: (row: Route) => (!row.sincroDepot ? '-' : row.sincroDepot.tag),
        },
        {
          title: () => (
            <Tooltip placement="topLeft" title={t('admin_router.header.zone_as')}>
              <span className="no-wrap-text">{t('admin_router.header.zone_as')}</span>
            </Tooltip>
          ),
          key: 'zone',
          render: (row: Route) =>
            !row.assignmentZone ? '-' : `${row.assignmentZone.name} (${row.assignmentZone.code})`,
        },
      ],
    },
    {
      title: t('admin_router.header.destination'),
      key: 'destino',
      children: [
        {
          title: t('admin_router.header.country'),
          dataIndex: 'land1z',
          key: 'paisDestino',
        },
        {
          title: t('admin_router.header.cp'),
          dataIndex: 'pstlzz',
          key: 'cpDestino',
        },
        {
          title: t('admin_router.header.city'),
          dataIndex: 'zcityCodez',
          key: 'ciudadDestino',
        },
        {
          title: t('admin_router.header.zone'),
          dataIndex: 'trfznz',
          key: 'zonaDestino',
        },
        {
          title: t('admin_router.header.region'),
          dataIndex: 'regioz',
          key: 'regionDestino',
        },
      ],
    },
    {
      title: () => (
        <Tooltip placement="topLeft" title={t('admin_router.header.service_type')}>
          {t('admin_router.header.serv_type')}
        </Tooltip>
      ),
      dataIndex: 'servTyp',
      key: 'tipo servicio',
    },
  ]

  const handleClearFiltersEquipment = () => {
    setFilters((prevState: RoutesFilters) => ({
      ...removeProperties(prevState, 'equipment'),
      page: 1,
    }))
  }

  const handleClearFiltersOrigin = () => {
    setFilters((prevState: RoutesFilters) => ({
      ...removeProperties(prevState, 'origin'),
      page: 1,
    }))
  }

  const handleClearFiltersDestination = () => {
    setFilters((prevState: RoutesFilters) => ({
      ...removeProperties(prevState, 'destination'),
      page: 1,
    }))
  }

  const handleOriginFilterChange = (value: any, option: any) => {
    if (value) {
      setFilters(({ asnum, ...prevState }: RoutesFilters) => ({
        ...removeProperties(prevState, 'origin'),
        page: 1,
        [option.type]: value as string,
      }))
    } else {
      handleClearFiltersOrigin()
    }
  }

  const handleDestinationFilterChange = (value: any, option: any) => {
    if (value) {
      setFilters(({ asnum, ...prevState }: RoutesFilters) => ({
        ...removeProperties(prevState, 'destination'),
        page: 1,
        [option.type]: value as string,
      }))
    } else {
      handleClearFiltersDestination()
    }
  }

  useEffect(() => {
    clearAllFilters()
  }, [clearAllFilters])

  useEffect(() => {
    if (
      location.pathname === PATHS.ADMIN.ROUTES ||
      location.pathname === PATHS.ADMINRO.ROUTES ||
      location.pathname === PATHS.PROCUREMENT.ROUTES
    ) {
      clearAllFilters()
    }
  }, [location.pathname, clearAllFilters])

  useEffect(() => {
    const handlePopState = () => {
      clearAllFilters()
    }

    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [clearAllFilters])

  useEffect(() => {
    const handleRouteFilter = (event: CustomEvent) => {
      handleAsnumFilterChange(event.detail)
    }

    window.addEventListener('routeFilter', handleRouteFilter as EventListener)
    return () => {
      window.removeEventListener('routeFilter', handleRouteFilter as EventListener)
    }
  }, [handleAsnumFilterChange])

  return (
    <>
      <ContentContainer className="admin-dashboard">
        <StyledRow className="sticky-options border-botom">
          {roles?.includes(ADMINRO) ? null : (
            <Tooltip placement="bottomLeft" title={t('globals.bulk_data_upload.modal_title')}>
              <StyledButton
                className="inline left-side-button"
                onClick={() => {
                  setBulkDataUploadModalIsVisible(true)
                }}
              >
                <DiffOutlined className="option-button" />
              </StyledButton>
            </Tooltip>
          )}
          <div
            className={`filters-container${
              filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
            }`}
          >
            <Button onClick={handleDisplayFilters} id="displayFiltersButton">
              <FilterOutlined />
            </Button>
            <div className="filters-list">
              <StyledSelect
                showSearch
                maxTagCount="responsive"
                allowClear
                placeholder={t('admin_router.filter_options.contract_filters.asnum')}
                options={routesFilters?.sapTptAsnums.map(arrayToOptions())}
                onChange={handleAsnumFilterChange}
                value={filters?.asnum}
              />
              <StyledSelect
                showSearch
                maxTagCount="responsive"
                allowClear
                onChange={handleOriginFilterChange}
                placeholder={t('admin_router.filter_options.origin')}
                optionFilterProp="label"
                options={[
                  {
                    label: <span>{t('globals.generics.city')}</span>,
                    title: 'originCityCode',
                    options: routesFilters?.originCityCodes.map(
                      ({ cityCode, region, description }) => ({
                        value: cityCode,
                        label: `${cityCode} - ${description}, ${region}`,
                        type: 'originCityCode',
                      }),
                    ),
                  },
                  {
                    label: <span>{t('globals.generics.zone')}</span>,
                    title: 'originZone',
                    options: routesFilters?.originZones.map(arrayToOptions('originZone')),
                  },
                  {
                    label: <span>{t('globals.generics.region')}</span>,
                    title: 'originRegion',
                    options: routesFilters?.originRegions.map(arrayToOptions('originRegion')),
                  },
                  {
                    label: <span>{t('globals.generics.post_code')}</span>,
                    title: 'originPostCode',
                    options: routesFilters?.originPostCodes.map(arrayToOptions('originPostCode')),
                  },
                ].filter(({ options }) => !!options?.length)}
                disabled={routesFiltersIsLoading}
                loading={routesFiltersIsLoading}
                value={
                  filters?.originPostCode ??
                  filters?.originRegion ??
                  filters?.originZone ??
                  filters?.originCityCode
                }
              />
              <StyledSelect
                showSearch
                maxTagCount="responsive"
                allowClear
                onChange={handleDestinationFilterChange}
                placeholder={t('admin_router.filter_options.destination')}
                optionFilterProp="label"
                options={[
                  {
                    label: <span>{t('globals.generics.city')}</span>,
                    title: 'destinationCityCode',
                    options: routesFilters?.destinationCityCodes.map(
                      ({ cityCode, region, description }) => ({
                        value: cityCode,
                        label: `${cityCode} - ${description}, ${region}`,
                        type: 'destinationCityCode',
                      }),
                    ),
                  },
                  {
                    label: <span>{t('globals.generics.zone')}</span>,
                    title: 'destinationZone',
                    options: routesFilters?.destinationZones.map(arrayToOptions('destinationZone')),
                  },
                  {
                    label: <span>{t('globals.generics.region')}</span>,
                    title: 'destinationRegion',
                    options: routesFilters?.destinationRegions.map(
                      arrayToOptions('destinationRegion'),
                    ),
                  },
                  {
                    label: <span>{t('globals.generics.post_code')}</span>,
                    title: 'destinationPostCode',
                    options: routesFilters?.destinationPostCodes.map(
                      arrayToOptions('destinationPostCode'),
                    ),
                  },
                ].filter(({ options }) => !!options?.length)}
                disabled={routesFiltersIsLoading}
                loading={routesFiltersIsLoading}
                value={
                  filters?.destinationPostCode ??
                  filters?.destinationRegion ??
                  filters?.destinationZone ??
                  filters?.destinationCityCode
                }
              />
              <StyledSelect
                showSearch
                maxTagCount="responsive"
                allowClear
                placeholder={t('admin_router.filter_options.contract_filters.equipment')}
                onChange={handleEquipmentFilterChange}
                value={filters?.equipment}
                options={equipmentOptions}
              />
            </div>
          </div>
        </StyledRow>
        <Spin spinning={isRoutesLoading}>
          <StyledRow className="data-table-container">
            <Table
              columns={columns}
              dataSource={routes?.routes}
              className="dataTable routesTable"
              rowKey="id"
              pagination={pagination}
              scroll={{ x: true }}
              style={{ width: '100%', height: '100%' }}
            />
          </StyledRow>
        </Spin>
      </ContentContainer>
      <BulkDataUploadModal
        isVisible={bulkDataUploadModalIsVisible}
        onClose={() => {
          setBulkDataUploadModalIsVisible(false)
        }}
        dataType={BulkDataTypesEnum.ROUTES}
      />
    </>
  )
}

export { RoutesDashboard }
